import { Injectable } from "@angular/core";
import { BehaviorSubject, Observable, Subject, Subscription } from "rxjs";
import { filter } from "rxjs/operators";
import { Injector } from "@angular/core";

import { ILiveIssueR } from "../interfaces/LiveIssueR";
import { IPlaybackAction } from "../interfaces/PlaybackAction";
import { SessionSocketService } from "./session-socket.service";
import { ToasterService } from "./toaster.service";
import { OverrideVariables } from "../interfaces/OverrideVariable";
import { IVariable } from "../interfaces/VariableDictionary";
import { SessionVariable } from "../interfaces/SessionVariable";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { environment } from "src/environments/environment";
import { AutoDialogResponseMode } from "src/app/shared/constants/constants";

@Injectable({
  providedIn: "root",
})
export class RecordingService {
  baseUrl = `${environment.baseUrl}`;
  isRecording = false;
  isPromptRecording = false;
  isSwipeUntil = new BehaviorSubject<boolean>(false); // bool to manage lab state that shows swipe-until UI
  isPinchZoomMode = new BehaviorSubject<boolean>(false);
  isContinueFromHere: BehaviorSubject<boolean> =  new BehaviorSubject<boolean>(false);
  isPromptRecordingStarted: BehaviorSubject<boolean> =  new BehaviorSubject<boolean>(false);
  isFreshRecordingStarted = new BehaviorSubject<boolean>(false);
  skipRecording = new BehaviorSubject<boolean>(false);
  isRecordedEnded = new BehaviorSubject<boolean>(false);
  onboardingComplete = new BehaviorSubject<boolean>(false);
  firstTestRecorded = new BehaviorSubject<boolean>(false);
  IssuesCreated = new BehaviorSubject<ILiveIssueR[]>([]);
  insertStepAtIndex = new BehaviorSubject<number>(0);
  isAutoDialogHandle: boolean = false;
  // Response strategy for auto-handled system dialog
  autoDialogResponseMode: AutoDialogResponseMode | null = null;
  isHighPromptMode: boolean = false;
  ManualRecording$?: Subscription;
  continueAppSwitch?: any;
  switchAppMood: any;
  manualStepsRecorded;
  public automatedStepsRecorded = new BehaviorSubject<IPlaybackAction[]>([]);
  private automatedRecordingSubscription?: Subscription;
  anyChangeInTestCaseStep: boolean = false;
  overrideTestDataVariables = new BehaviorSubject<OverrideVariables[]>([]);

  constructor(private injector: Injector, private toasterService: ToasterService,private http: HttpClient) {
    this.initializeAutomatedRecording();
  }

  private initializeAutomatedRecording(): void {
    const sessionSocket = this.injector.get(SessionSocketService);

    this.automatedRecordingSubscription = sessionSocket.SocketMessagesText.pipe(
      filter(
        (message) =>
          message.startsWith("fetch_data_emit") || message.startsWith("record")
      )
    ).subscribe((message) => {
      if (message.startsWith("fetch_data_emit")) {
        const recordStep =
          message.length > 16 ? JSON.parse(message.substr(15)) : null;
        if (recordStep) {
          const allSteps = this.automatedStepsRecorded.value.slice();
          let index = recordStep.stepIndex
          if(recordStep.replaceStep){
            // Edit
            allSteps.splice(index, 1, recordStep);
            this.toasterService.ShowToastMessage("Step Edited Successfully", 1, 5000);
          }
          else{ 
            // Insert
            allSteps.splice(index, 0, recordStep);
          }
          this.automatedStepsRecorded.next(allSteps);

          if(['OVERRIDE_TEST_DATA_VARIABLE','OVERRIDE_TEST_CASE_VARIABLE','OVERRIDE_ARITHMETIC_VARIABLE','OVERRIDE_API_VARIABLE'].includes(recordStep.actionType)){
            let overrideVars = this.overrideTestDataVariables.value.slice();
            const index = overrideVars.findIndex(
              (override: OverrideVariables) => override.variableName === recordStep.variableName
            );
            const overrideVar = {
              variableName: recordStep.variableName,
              overrideValue: recordStep.overrideValue
            };
          
            if (index !== -1) {
              overrideVars.splice(index, 1, overrideVar);
            } else {
              overrideVars.push(overrideVar);
            }
            this.overrideTestDataVariables.next(overrideVars);
          }
        }
      }
    });
  }

  AddIssue(_issue: ILiveIssueR): void {
    const allIssues = this.IssuesCreated.getValue();
    allIssues.push(_issue);
    this.IssuesCreated.next(allIssues);
  }

  AddIssues(_issues: ILiveIssueR[]): void {
    let allIssues = this.IssuesCreated.getValue();
    allIssues = allIssues.concat(_issues);
    this.IssuesCreated.next(allIssues);
  }

  GetIssuesCreatedCount(): number {
    return this.IssuesCreated.getValue().length;
  }

  hasCreatedAnIssue(): boolean {
    return this.IssuesCreated.getValue().length > 0;
  }

  ResetIssues(): void {
    this.IssuesCreated.next([]);
  }

  ResetManualSteps(): void {
    this.manualStepsRecorded.next([]);
  }

  ResetAutomatedRecording() {
    this.automatedStepsRecorded.next([]);
    this.overrideTestDataVariables.next([]);
    this.isAutoDialogHandle = false;
    this.autoDialogResponseMode = null;
  }

  manageSwipeUntilUI(display: boolean): void {
    this.isSwipeUntil.next(display); // true to show swipe until ui
  }

  managePinchZoomMode(active: boolean): void {
    this.isPinchZoomMode.next(active);
  }
  validateCurlRequest(curlRequest: string, validationType: string, pathQuery?: string): Observable<any> {
    const apiUrl = `${this.baseUrl}/automation-microservice/test-cases/sap-cloud/validate-curl`;
    const headers = new HttpHeaders({
      'Content-Type': 'application/json',
    });

    const body = {
      curl_request: curlRequest,
      validation_type: validationType,
      json_path_query: pathQuery ? pathQuery : ''
    };

    return this.http.post(apiUrl, body, { headers });
  }
  ngOnDestroy(): void {
    if (this.automatedRecordingSubscription) {
      this.automatedRecordingSubscription.unsubscribe();
    }
  }
}
